import { useEffect, useState } from 'react';
import type { Building } from '@wemap/core';
import type { WemapMap } from '@wemap/map';
import './LevelSwitcher.css';

type LevelSwitcherProps = {
    /** The map whose current building/level drives the switcher. */
    wemapMap: WemapMap | null | undefined;
};

/**
 * Floating indoor level switcher for the new `@wemap/map` SDK.
 *
 * The old livemap rendered a native level control; `WemapMap` does not, so this
 * component recreates it: one button per level of the current building, ordered
 * top floor first, calling {@link WemapMap.setLevel} on click. It is
 * self-contained — pass a `wemapMap` and it subscribes to building/level changes
 * on its own. Renders nothing when there is no indoor building in view.
 */
export default function LevelSwitcher({ wemapMap }: LevelSwitcherProps) {
    const [building, setBuilding] = useState<Building | null>(null);
    const [activeLevel, setActiveLevel] = useState<number | null>(null);

    useEffect(() => {
        if (!wemapMap) {
            setBuilding(null);
            setActiveLevel(null);
            return;
        }

        setBuilding(wemapMap.getCurrentBuilding());
        setActiveLevel(wemapMap.getLevel());

        const unsubscribeBuilding = wemapMap.onBuildingChange(setBuilding);
        const unsubscribeLevel = wemapMap.onLevelChange(setActiveLevel);

        return () => {
            unsubscribeBuilding();
            unsubscribeLevel();
        };
    }, [wemapMap]);

    if (!wemapMap || !building || building.levels.length === 0) {
        return null;
    }

    return (
        <div className="wemap-level-switcher">
            {[...building.levels]
                .sort((a, b) => b.level - a.level)
                .map((lvl) => (
                    <button
                        key={lvl.id}
                        type="button"
                        title={lvl.name}
                        className={`wemap-level-switcher__btn${lvl.level === activeLevel ? ' is-active' : ''}`}
                        onClick={() => wemapMap.setLevel(lvl.level)}
                    >
                        {lvl.short_name}
                    </button>
                ))}
        </div>
    );
}
